/**
 * @file この資料で書き込みのあるページの番号と、ページごとの過去の回を持つ hook である。
 *
 * @what: (家族, まなぶ人, 資料) が変わるたびに Storage の一覧を取り、いまの線があるページの集合と、
 *   ページごとの過去の回の番号を返す。書いたその場で印が付くよう、いま開いているページの有無
 *   (mark) と、過去の回の増減 (addAttempt / removeAttempt) は外から直せる。
 *   「書き込みのあるページ」には、いまの線が空でも過去の回が残っているページを含める。
 *   ※なぜなら解き直しの最中のページが一覧から消えると、戻る手がかりが無くなるためである。
 * @why: 書き込みは目次の下に「書き込みのあるページ」として並べ、そこへ戻れるようにする。
 *   一覧を毎回取り直すと書くたびに通信が増えるので、開いたときだけ取り、あとは手元で直す。
 * @failure: 一覧の取得に失敗したら空のまま扱う (印が出ないだけで、書き込み自体は読める)。
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { listPageInkEntries } from "../lib/pageInk";

const NO_ATTEMPTS: readonly number[] = [];

export function usePageInkPages(familyId: string, learnerId: string | null, sourceId: string) {
  const [inked, setInked] = useState<ReadonlySet<number>>(() => new Set());
  const [attempts, setAttempts] = useState<ReadonlyMap<number, readonly number[]>>(() => new Map());

  useEffect(() => {
    // 資料やまなぶ人が変わったら、前の一覧を出したままにしない。
    setInked(new Set());
    setAttempts(new Map());
    if (learnerId === null) return;
    let cancelled = false;
    listPageInkEntries(familyId, learnerId, sourceId)
      .then((found) => {
        if (cancelled) return;
        setInked(new Set(found.inked));
        setAttempts(found.attempts);
      })
      .catch((err: unknown) => {
        console.error("[study] failed to list page ink", err);
        if (!cancelled) {
          setInked(new Set());
          setAttempts(new Map());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [familyId, learnerId, sourceId]);

  /** いまの線があるか、過去の回が残っているページの番号 (昇順)。 */
  const pages = useMemo(() => {
    const all = new Set(inked);
    for (const [page, list] of attempts) if (list.length > 0) all.add(page);
    return [...all].sort((a, b) => a - b);
  }, [inked, attempts]);

  /** いま開いているページに線があるかを、保存の結果に合わせて直す。 */
  const mark = useCallback((page: number, hasInk: boolean) => {
    setInked((prev) => {
      if (prev.has(page) === hasInk) return prev;
      const next = new Set(prev);
      if (hasInk) next.add(page);
      else next.delete(page);
      return next;
    });
  }, []);

  /** そのページに残してある過去の回の番号 (昇順)。 */
  const attemptsOf = useCallback(
    (page: number): readonly number[] => attempts.get(page) ?? NO_ATTEMPTS,
    [attempts],
  );

  const addAttempt = useCallback((page: number, attempt: number) => {
    setAttempts((prev) => {
      const list = prev.get(page) ?? [];
      if (list.includes(attempt)) return prev;
      const next = new Map(prev);
      next.set(page, [...list, attempt].sort((a, b) => a - b));
      return next;
    });
  }, []);

  const removeAttempt = useCallback((page: number, attempt: number) => {
    setAttempts((prev) => {
      const list = prev.get(page);
      if (!list || !list.includes(attempt)) return prev;
      const next = new Map(prev);
      const rest = list.filter((n) => n !== attempt);
      if (rest.length === 0) next.delete(page);
      else next.set(page, rest);
      return next;
    });
  }, []);

  return { pages, mark, attemptsOf, addAttempt, removeAttempt };
}
